import { Fragment, useEffect, useState } from 'react';

import Text from '@cartwhl/ui/base/Text';
import Dropdown from '@cartwhl/ui/atoms/Dropdown';
import ChevronDownIcon from '@cartwhl/ui/icons/ChevronDownIcon';

import type { PluginDownloadOption, PluginPlatform } from '../integrationsPageData';
import {
  detectPluginPlatform,
  getDefaultDownloadOption,
  getPluginVersionMenuLabel,
  groupPluginDownloadOptionsByPlatform,
  pluginPlatformHeaderLabels,
  pluginPlatformIcons,
} from '../pluginDownloadUtils';

interface PluginDownloadButtonProps {
  options: PluginDownloadOption[];
  className?: string;
}

function DownloadOptionLabel({
  option,
  light = false,
}: {
  option: PluginDownloadOption;
  light?: boolean;
}) {
  const platformColorClass = light ? 'text-white' : undefined;
  const versionColorClass = light ? 'text-[#F6F6F6]' : undefined;

  return (
    <span className="inline-flex min-w-0 items-baseline gap-1">
      <Text
        as="span"
        variant="studio/body/medium/sm"
        className={['truncate', platformColorClass].filter(Boolean).join(' ')}
      >
        {option.platformLabel}
      </Text>
      <span
        className={[
          'font-sans shrink-0 text-[12px]/[16px] font-medium',
          versionColorClass,
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {option.versionLabel}
      </span>
    </span>
  );
}

function PlatformGroupHeader({ platform }: { platform: PluginPlatform }) {
  const PlatformIcon = pluginPlatformIcons[platform];

  return (
    <div
      aria-hidden
      className="grid w-full min-w-[260px] grid-cols-[20px_minmax(0,1fr)] items-center gap-x-3 px-4 py-2"
    >
      <span className="flex h-5 w-5 shrink-0 items-center justify-center">
        <PlatformIcon className="text-gray-45 h-4 w-4" />
      </span>
      <Text
        as="span"
        variant="studio/body/medium/sm"
        className="text-gray-45 text-left"
      >
        {pluginPlatformHeaderLabels[platform]}
      </Text>
    </div>
  );
}

export default function PluginDownloadButton({
  options,
  className = '',
}: PluginDownloadButtonProps) {
  const [defaultOption, setDefaultOption] = useState(options[0]);

  useEffect(() => {
    setDefaultOption(getDefaultDownloadOption(options, detectPluginPlatform()));
  }, [options]);

  const DefaultPlatformIcon = pluginPlatformIcons[defaultOption.platform];

  return (
    <div
      className={`focus-within:ring-blue/40 flex h-10 w-full items-stretch overflow-hidden rounded-lg focus-within:ring-2 ${className}`}
    >
      <a
        href={defaultOption.href}
        className="cw-text-box-none focus-visible relative inline-flex min-h-0 min-w-0 flex-1 items-center justify-center gap-2 rounded-l-lg bg-blue px-4 text-center transition-all duration-200 ease-in-out hover:bg-[#166ADC] active:scale-[0.99]"
      >
        <DefaultPlatformIcon className="h-4 w-4 shrink-0 text-white" />
        <DownloadOptionLabel option={defaultOption} light />
      </a>

      <Dropdown
        align="end"
        sideOffset={4}
        trigger={
          <button
            type="button"
            aria-label="Choose Unreal Engine version"
            className="cw-text-box-none focus-visible inline-flex min-h-0 shrink-0 items-center justify-center self-stretch rounded-r-lg border-l border-white/20 bg-blue px-3 text-white transition-all duration-200 ease-in-out hover:bg-[#166ADC] active:scale-[0.99]"
          >
            <ChevronDownIcon className="h-4 w-4" />
          </button>
        }
      >
        {groupPluginDownloadOptionsByPlatform(options).map(
          (platformOptions, groupIndex) => {
            const platform = platformOptions[0].platform;

            return (
              <Fragment key={platform}>
                {groupIndex > 0 ? <Dropdown.Separator /> : null}
                <PlatformGroupHeader platform={platform} />
                {platformOptions.map((option) => (
                  <Dropdown.Item key={option.id} href={option.href}>
                    {getPluginVersionMenuLabel(option)}
                  </Dropdown.Item>
                ))}
              </Fragment>
            );
          },
        )}
      </Dropdown>
    </div>
  );
}
